import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/utils';
import { Property } from '@/types/property';
import { MapPin } from 'lucide-react';
import type mapboxgl from 'mapbox-gl'; // Import type for mapboxgl
import { useEffect, useRef, useState } from 'react';

// VITE_MAPBOX_TOKEN is still needed, ensure it's in your .env file

interface PropertyMapProps {
    property: Property;
    address: string;
    lat: number;
    lng: number;
}

export default function PropertyMap({ property, address, lat, lng }: PropertyMapProps) {
    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null); // Renamed for clarity
    const markerRef = useRef<mapboxgl.Marker | null>(null); // Renamed for clarity
    const [mapboxglLib, setMapboxglLib] = useState<typeof mapboxgl | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const styleElementRef = useRef<HTMLStyleElement | null>(null);

    useEffect(() => {
        let isMounted = true;
        const loadMapbox = async () => {
            try {
                const mapbox = (await import('mapbox-gl')).default;
                await import('mapbox-gl/dist/mapbox-gl.css');

                if (!isMounted) return;

                mapbox.accessToken = import.meta.env['VITE_MAPBOX_TOKEN'];
                setMapboxglLib(mapbox);
            } catch (error) {
                console.error('Failed to load Mapbox GL:', error);
                if (isMounted) setIsLoading(false);
            }
        };

        loadMapbox();

        return () => {
            isMounted = false;
            if (markerRef.current) {
                markerRef.current.remove();
                markerRef.current = null;
            }
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
            if (styleElementRef.current) {
                document.head.removeChild(styleElementRef.current);
                styleElementRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!mapboxglLib || !mapContainer.current || mapRef.current) return;

        try {
            mapRef.current = new mapboxglLib.Map({
                container: mapContainer.current,
                style: 'mapbox://styles/mapbox/streets-v12',
                center: [lng, lat],
                zoom: 15,
                failIfMajorPerformanceCaveat: true,
                attributionControl: false
            });

            const style = document.createElement('style');
            style.textContent = `
                .marker {
                    will-change: transform;
                    transform-origin: bottom center;
                }
                .marker-content {
                    white-space: nowrap;
                    background: hsl(var(--primary));
                    color: hsl(var(--primary-foreground));
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.5rem;
                    font-weight: 600;
                    font-size: 0.875rem;
                    line-height: 1.25rem;
                    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
                }
            `;
            document.head.appendChild(style);
            styleElementRef.current = style;

            mapRef.current.on('load', () => {
                if (mapRef.current && mapboxglLib) { // Ensure mapboxglLib is available
                    mapRef.current.addControl(new mapboxglLib.NavigationControl());
                    const markerEl = document.createElement('div');
                    markerEl.className = 'marker';
                    markerEl.innerHTML = `<div class="marker-content">${formatPrice(property.list_price)}</div>`;
                    
                    markerRef.current = new mapboxglLib.Marker({
                        element: markerEl,
                        anchor: 'bottom',
                    }).setLngLat([lng, lat]).addTo(mapRef.current);
                }
                setIsLoading(false);
            });

            mapRef.current.on('error', (e) => {
                console.error('Mapbox error:', e);
                setIsLoading(false);
            });

        } catch (error) {
            console.error('Error initializing map:', error);
            setIsLoading(false);
        }
    }, [mapboxglLib, lat, lng, property.list_price]); // Added mapboxglLib to dependencies

    return (
        <div className="bg-slate-100 rounded-md overflow-hidden relative h-[250px]">
            <div ref={mapContainer} className="w-full h-full" />
            {isLoading && (
                <div className="absolute inset-0 bg-muted flex items-center justify-center z-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-2"></div>
                    <div className="text-muted-foreground">Loading map...</div>
                </div>
            )}
            {!isLoading && mapboxglLib && (
                 <div className="absolute bottom-4 right-4">
                    <Button
                        variant="outline"
                        className="bg-white shadow-sm border-0"
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank')}
                    >
                        <MapPin className="h-5 w-5 mr-2 text-gray-700" />
                        View on map
                    </Button>
                </div>
            )}
        </div>
    );
};

